import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useMemo } from 'react';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import useConciergeTiles from './useConciergeTiles';
import { seededShuffle, seededUnit } from '@apps/card/utils/seededRandom';
import { useDesignGalleryFiltersContext } from './DesignGalleryFiltersProvider';
import { cloneDeep } from 'lodash-es';
import { useDefaultMediaMatchContext } from '@shared/utils/media/DefaultMediaMatchProvider';
import type { GalleryItemData } from './components/GalleryItem/GalleryItem';
import { useCardCategoryContext } from '@apps/card/components/CardCategoryProvider';
import { useGalleryPlacementSeed } from './useGalleryPlacementSeed';
import { useValuePropsPayload } from './useValuePropsPayload';
import type { PropAsset, PropCarouselAsset, ValuePropLike } from './propValues.types';
import { assetForBreakpoint, getVisibleSlides, isCarouselAsset, isValuePropLike } from './propValues.helpers';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';

// Value-prop tiles come in two shapes: a single prop (`prop`) and the auto-rotating list
// (`propCarousel`). Both are laid out by `gridArea`, so the layout pass must treat them identically.
// A carousel has no top-level contentList; it is renderable when at least one slide has an asset
// for the current breakpoint, using the same `getVisibleSlides` logic ValuePropListCard renders by.
const isRenderableValueProp = (prop: ValuePropLike, isMobile: boolean) =>
  prop.type === 'propCarousel' ? getVisibleSlides(prop.slides, isMobile).length > 0 : !!assetForBreakpoint(prop.contentList, isMobile);

// Drop promos that can't render at this breakpoint, and keep the rest within the rows the regular
// tiles fill. They remain extras, but cannot create distant rows below a short gallery or sustain
// a tail of repeated promos.
const keepRenderableValueProps = (items: ReadonlyArray<GalleryItemData>, columnCount: number, isMobile: boolean): GalleryItemData[] => {
  const regularTileRows = Math.ceil(items.filter(item => !isValuePropLike(item)).length / columnCount);
  return items.filter(item => !isValuePropLike(item) || (isRenderableValueProp(item, isMobile) && item.row < regularTileRows));
};

const RESPONSIVE_CONFIG = {
  _: { row: 2, rowGap: 2 },
  sm2: { row: 3, rowGap: 2 },
  md3: { row: 4, rowGap: 1 },
  lg: { row: 5, rowGap: 1 },
  xl: { row: 6, rowGap: 1 },
};

export const useGalleryItemsWithValueProps = (items: ReadonlyArray<GalleryItemData>, isSearchActive: boolean) => {
  const { currentCategory: category } = useCardCategoryContext();
  const payload = useValuePropsPayload();
  const { getTotalActiveFilterCount } = useDesignGalleryFiltersContext();
  const injectConcierge = useConciergeTiles();
  // Promo columns and the repeat shuffle come from the placement seed (PRNT-5494), so they are the
  // same on the server and the client and hold still for the placement period. Separate sub-seeds
  // (:columns, :shuffle), so a card's column and its place in the shuffle aren't the same number.
  const seed = useGalleryPlacementSeed();
  const isMobile = useIsMobileScreen();

  const isCategoryWithConciergeService = [StationeryTemplateCategoryEnum.invitation, StationeryTemplateCategoryEnum.saveTheDate].includes(category);
  const conciergeEnabled = isCategoryWithConciergeService;

  const defaultScreen = useDefaultMediaMatchContext()?.defaultScreen;

  // row - number of cards per row, rowGap - number of empty rows between cards
  const [rowConfig] = useResponsive(
    {
      values: RESPONSIVE_CONFIG,
    },
    defaultScreen === 'desktop' ? RESPONSIVE_CONFIG.lg : defaultScreen === 'tablet' ? RESPONSIVE_CONFIG.md3 : RESPONSIVE_CONFIG._,
  );

  const shouldInjectProps = getTotalActiveFilterCount() === 0 && !isSearchActive;

  const tiles = useMemo(() => {
    if (!shouldInjectProps) {
      for (const item of items) {
        // Allow the prop cards to flow during search, -1 signals no position.
        if (item.type === 'prop') {
          item.column = -1;
          item.row = -1;
        }
      }
      return items;
    }

    const templatesWithProps = [...items];

    let valueProps: (PropAsset | PropCarouselAsset)[] = [];

    const totalRows = items.length / (rowConfig?.row ?? 1);
    const requiredPropsToFillGallery = totalRows / ((rowConfig?.rowGap ?? 1) + 1);

    if (payload) {
      const originalValuePropsList = payload.props;

      valueProps = cloneDeep([...originalValuePropsList]);

      if (payload.repeatTiles) {
        const shuffledPool = seededShuffle(originalValuePropsList, `${seed}:shuffle`);

        if (valueProps.length < requiredPropsToFillGallery) {
          for (let i = valueProps.length; i < requiredPropsToFillGallery; i++) {
            const card = shuffledPool[i % originalValuePropsList.length];
            // Do not repeat the opening tile.
            if (!card.pinToStart) {
              valueProps.push({ ...card });
            }
          }
        }
      }
    }

    if (rowConfig) {
      let regularPropIndex = 0;
      // Make sure we do not place more props than can fit.
      const maxRows = (templatesWithProps.length + valueProps.length) / (rowConfig.row * (rowConfig.rowGap + 1)) - 1;

      valueProps.forEach((card, row) => {
        if (row >= maxRows) {
          return;
        }

        if (card.pinToStart) {
          card.row = 0;
          card.column = 0;
        } else {
          const availableColumns = Math.max(rowConfig.row - (card.columns ?? 1) + 1, 1);
          card.column = Math.floor(seededUnit(`${seed}:columns`, row) * availableColumns);
          // Start regular props on visible row 4, after the carousel and price tile; rows are zero-based.
          card.row = 3 + (rowConfig.rowGap + 1) * regularPropIndex;
          regularPropIndex++;
        }
        templatesWithProps.push(isCarouselAsset(card) ? { ...card, type: 'propCarousel' } : { ...card, type: 'prop' });
      });
    }

    const tilesWithConcierge = conciergeEnabled ? injectConcierge(templatesWithProps) : templatesWithProps;
    return keepRenderableValueProps(tilesWithConcierge, rowConfig?.row ?? 1, isMobile);
  }, [items, shouldInjectProps, rowConfig, payload, conciergeEnabled, seed, injectConcierge, isMobile]);

  return {
    itemsWithProps: tiles,
    galleryColumnCount: rowConfig.row,
  };
};
